import { useEffect, useState } from "react";

const STORAGE_KEY = "hl.maintenanceBypass";
// Two keys, not three: some keyboards drop the third simultaneous keypress.
const COMBO = ["h", "l"];

const readStoredBypass = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const storeBypass = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // Private-mode / blocked storage: the bypass still holds for this mount.
  }
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

/**
 * Escape hatch for the full-screen maintenance takeover: holding H + L together
 * dismisses it for the rest of the tab session (survives reloads via
 * sessionStorage, cleared when the tab closes).
 */
export const useMaintenanceBypass = (): boolean => {
  const [bypassed, setBypassed] = useState(readStoredBypass);

  useEffect(() => {
    if (bypassed) return;

    const held = new Set<string>();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || isTypingTarget(event.target)) return;
      held.add(event.key.toLowerCase());
      if (COMBO.every((key) => held.has(key))) {
        storeBypass();
        setBypassed(true);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      held.delete(event.key.toLowerCase());
    };

    const onBlur = () => held.clear();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [bypassed]);

  return bypassed;
};
